import React, { useCallback, useEffect, useMemo } from 'react';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { selectableUnitHarmonizer } from '@shared/helpers/unit.helpers';
import {
  useElementExpressionVariablesById,
  useSolveExpressionError,
} from '@client/hooks/use-element-expression-variables.hooks';
import { IElementExpressionProperty } from '@shared/models/element-property.model';
import { useCircularError } from '@client/hooks/use-element-properties.hooks';
import {
  getCount,
  isElementExpressionProperty,
  isElementPropertySourceCategory,
  isElementPropertySourceNone,
  isFullyEditableProperty,
} from '@shared/helpers/element-property.helpers';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import {
  ExpressionInput,
  type ExpressionInputProps,
} from '@features/expressions/ExpressionInput/ExpressionInput';
import { type ExpressionInputChangeEvent } from '@features/expressions/ExpressionInput/expression-input-change.helpers';
import { Icon, isQuantityIconName } from '@ui/Icon/Icon';
import { useApplyProjectChanges } from '@client/store/project';
import { resolvePropertyModification } from './element-property-expression.helpers';
import { propertyChangesToProjectChanges } from '@shared/helpers/pipeline/property-change-to-project-change.helpers';
import {
  getQuantityDisplayUnitForEdit,
  isElementQuantityName,
} from '@shared/helpers/element-quantity.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { getPropertyDisplayName } from '@shared/helpers/element-property-display-name.helpers';

interface ElementPropertyExpressionProps {
  property: IElementExpressionProperty;
  element: OneOfPropertyElements;
  readonly?: boolean;
  onErrorMessageChange?: (message: string | undefined) => void;
  showIcon?: boolean;
  /** deactivates the input, without affecting styling */
  deactivated?: boolean;
  /** passthrough to ExpressionInput */
  unitAlignment?: 'tryVertical' | 'none';
  onUnitClick?: ExpressionInputProps['onUnitClick'];
  /** forces active styling regardless of focus/hover state */
  active?: boolean;
}

const ElementPropertyExpression: React.FC<ElementPropertyExpressionProps> = ({
  property,
  element,
  readonly: readonlyParam,
  onErrorMessageChange,
  showIcon = false,
  unitAlignment = 'tryVertical',
  deactivated = false,
  onUnitClick,
  active,
}) => {
  const { id, name, count, fallbackCount, min, max, category_id } = property;

  const isFallbackProperty =
    isElementExpressionProperty(property) && !property.count;

  const applyChanges = useApplyProjectChanges({ showErrorsInSnackbar: true });

  const isReadonlyElement = useIsReadonlyElement(element);
  const readonly = readonlyParam || isReadonlyElement || property.readonly;
  const { value, expression } = getCount(property);

  /** this is funky stuff. I have no idea what isFullyEditableProperty does, but here we use it to determine if the unit is editable. */
  const isUnitEditable = isFullyEditableProperty(property);

  // Get all variables that might be used in the expression (exluding self)
  const variables = useElementExpressionVariablesById(
    element.id,
    name,
    category_id,
  );

  const circularError = useCircularError(element, property, expression);
  const solveError = useSolveExpressionError(value, expression, {
    variables,
    min,
    max,
  })?.message;

  const expressionError = circularError ?? solveError;

  useEffect(() => {
    onErrorMessageChange?.(expressionError);
  }, [expressionError, onErrorMessageChange]);

  const onExpressionInputChange = useCallback(
    async (event: ExpressionInputChangeEvent): Promise<void> => {
      const modified = resolvePropertyModification(event, property, element);
      const projectChanges = propertyChangesToProjectChanges(element, [
        { ...property, ...modified },
      ]);

      if (projectChanges.length > 0) {
        const applyResult = await applyChanges(projectChanges);
        if (applyResult.error) return;
        if (applyResult.cancelled) return;
      }
    },
    [element, property, applyChanges],
  );

  // Category properties might have other units than the ones selectable in the unit dropdown
  const unit = isElementPropertySourceCategory(property)
    ? property.unit
    : selectableUnitHarmonizer(property.unit);

  const startIcon = showIcon && isQuantityIconName(property.name) && (
    <Icon category="quantity" icon={property.name} />
  );

  const displayUnit = useMemo(() => {
    if (isElement(element) && isElementQuantityName(property.name)) {
      return getQuantityDisplayUnitForEdit(element, property.name);
    }
    return property.displayUnit;
  }, [element, property.displayUnit, property.name]);

  return (
    <ExpressionInput
      propertyId={id}
      // Element + property name stays put across enrichment; property.id does not.
      id={`${element.id}-${name}`}
      // The row's name is a separate readonly field, so the value input needs its own
      aria-label={getPropertyDisplayName(property, element)}
      deactivated={deactivated}
      active={active}
      value={count}
      fallbackValue={fallbackCount}
      variables={variables}
      startIcon={startIcon}
      unit={
        isElementPropertySourceNone(property) && unit !== '%' ? undefined : unit
      }
      displayUnit={displayUnit}
      unitEditable={isUnitEditable}
      disabled={readonly}
      textAlignment="left"
      unitAlignment={unitAlignment}
      // Circular errors show even on fallback-only rows — the system-assigned
      // fallbackCount is often the edge that closes the loop (DEV-3642)
      errorMessage={isFallbackProperty ? circularError : expressionError}
      onChange={onExpressionInputChange}
      min={min}
      max={max}
      onUnitClick={onUnitClick}
    />
  );
};

export default ElementPropertyExpression;
